import './style.css'

import { BUTTON_ACTION_TYPE, FEATURE, MODULE_TYPE, NAVIGATOR_AUTHOR, PostMessageType } from '@abiglobalhealth/go-types'
import { FormEvent, Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactTextareaAutosize from 'react-textarea-autosize'

import { finishHealthNavigator, sendHealthNavigatorMessage, startHealthNavigator } from '@/api/navigator'
import { ConfirmationModal } from '@/components/ConfirmationModal'
import NavigatorIllustration from '@/components/icons/consultations/NavigatorIllustration'
import PlaneIcon from '@/components/icons/PlaneIcon'
import { RefreshIcon } from '@/components/icons/RefreshIcon'
import WritingAnimation from '@/components/icons/WritingAnimation'
import { ScrollToBottomButton } from '@/components/ScrollToBottomButton'
import { useConfig } from '@/hooks/useConfig'
import { useModule } from '@/hooks/useModule'
import { useNavigate } from '@/hooks/useNavigate'
import useNavigator from '@/hooks/useNavigator'
import { usePostMessage } from '@/hooks/usePostMessage'
import { useScrollToBottom } from '@/hooks/useScrollToBottom'
import { useUser } from '@/hooks/useUser'
import { getSlugTranslation } from '@/locale/utils'
import { isAbortError } from '@/utils/errors'
import { cn } from '@/utils/tailwind'

import { Bubble } from './Bubble'
import { HealthResourceButton } from './HealthResourceButton'
import { Message } from './Message'
import { MessageFeedback } from './MessageFeedback'
import { NavigatorBg } from './NavigatorBg'
import { SessionFeedback } from './SessionFeedback'

const ANIMATIONS_TIME = {
  LOADING: 300,
  MESSAGE: 2300,
  DISCLAIMER: 3300
}

export default function Navigator() {
  const { t } = useTranslation()
  const { user } = useUser()
  const { currentModule } = useModule()
  const { navigateToStep } = useNavigate()
  const {
    handleConversationStarted,
    handleConversationEnded,
    onGoingConversation,
    messages,
    navigator,
    setNavigator,
    loading,
    setLoading,
    isPending,
    pendingMessage,
    setPendingMessage
  } = useNavigator()
  const [firstTime, setFirstTime] = useState<boolean | undefined>()
  const [time, setTime] = useState<number>(ANIMATIONS_TIME.LOADING)
  const [inputMessage, setInputMessage] = useState<string>(pendingMessage || '')
  const isFinished = useMemo(() => navigator.healthNavigator.isFinished, [navigator])
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const lastMessageRef = useRef<HTMLDivElement>(null)
  const { containerRef: messageListRef, showScrollButton, scrollToBottom } = useScrollToBottom(`${navigator.healthNavigator.messages.length}`)
  const { sendPostMessage } = usePostMessage()
  const abortControllerRef = useRef<AbortController | null>(null)
  const initializedRef = useRef(false)
  const { config } = useConfig()
  const feedbackEnabled = useMemo(() => config?.features?.includes(FEATURE.HEALTH_NAVIGATOR_MESSAGE_FEEDBACK) ?? false, [config])
  const sessionFeedbackEnabled = useMemo(() => config?.features?.includes(FEATURE.HEALTH_NAVIGATOR_CONVERSATION_FEEDBACK) ?? false, [config])
  const [sessionFeedbackVisible, setSessionFeedbackVisible] = useState(false)
  const showSessionFeedback = useMemo(
    () => !!(isFinished && sessionFeedbackEnabled && navigator.healthNavigator.conversationId && !navigator.healthNavigator.userFeedback),
    [isFinished, sessionFeedbackEnabled, navigator.healthNavigator.conversationId]
  )

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
  }, [])

  const sendMessage = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault()

    if (!user?.data.userId) {
      setPendingMessage(inputMessage)
      if (currentModule?.type === MODULE_TYPE.CONSULTATION_LANDING && currentModule.buttonAction.type === BUTTON_ACTION_TYPE.NEXT_STEP) {
        const firstRoute = currentModule.buttonAction.routes?.[0]
        if (firstRoute?.route?.step) navigateToStep(firstRoute.route.step)
      }
      return
    }

    setLoading(true)
    setNavigator((prev) => {
      if (!prev) return prev

      return {
        ...prev,
        healthNavigator: {
          ...prev.healthNavigator,
          messages: [
            ...prev.healthNavigator.messages,
            {
              sender: NAVIGATOR_AUTHOR.PATIENT,
              text: inputMessage
            }
          ]
        }
      }
    })
    setInputMessage('')

    try {
      abortControllerRef.current = new AbortController()

      await sendHealthNavigatorMessage(
        {
          uniqueId: user?.data.uniqueId!,
          text: inputMessage
        },
        abortControllerRef.current.signal
      )
      if (!navigator.healthNavigator.messages.length) await sendPostMessage(PostMessageType.NAVIGATOR_CONVERSATION_STARTED)
    } catch (error: unknown) {
      if (!isAbortError(error)) throw error
    } finally {
      setLoading(false)
      abortControllerRef.current = null
    }
  }

  const initializeNavigator = async () => {
    try {
      abortControllerRef.current = new AbortController()
      const response = await startHealthNavigator({ uniqueId: user?.data.uniqueId! })
      if (response.healthNavigator.messages.length > 1) handleConversationStarted()
      setFirstTime(response.isFirstTime)
      setNavigator(response)
      abortControllerRef.current = null

      if (pendingMessage && user?.data.userId) {
        sendMessage()
        setPendingMessage(null)
      }
    } catch (error) {
      console.error('[Navigator] Error initializing', error)
    }
  }

  const handleFinish = async () => {
    try {
      abortControllerRef.current = new AbortController()

      const navigatorResponse = await finishHealthNavigator({ uniqueId: user?.data.uniqueId! }, abortControllerRef.current.signal)
      await sendPostMessage(PostMessageType.NEW_NAVIGATOR_SESSION_CLICK, { conversationId: navigator.healthNavigator.conversationId! })
      setNavigator(navigatorResponse)
      setIsConfirmationModalOpen(false)
      await handleConversationEnded()
      abortControllerRef.current = null
      await initializeNavigator()
    } catch (error: unknown) {
      if (!isAbortError(error)) throw error
    }
  }

  useEffect(() => {
    lastMessageRef?.current?.scrollIntoView({ behavior: 'smooth' })
  }, [navigator.healthNavigator.messages.length, lastMessageRef?.current])

  useEffect(() => {
    if (firstTime === false) return setTime(ANIMATIONS_TIME.DISCLAIMER)
    if (firstTime === undefined) return

    setTime(0)

    const interval = setInterval(() => {
      setTime((prev) => {
        if (prev > ANIMATIONS_TIME.DISCLAIMER) clearInterval(interval)
        return prev + 100
      })
    }, 100)

    return () => {
      setTime(ANIMATIONS_TIME.DISCLAIMER)
      clearInterval(interval)
    }
  }, [firstTime])

  useEffect(() => {
    if (initializedRef.current || !user?.data.uniqueId) return
    initializedRef.current = true
    initializeNavigator()
  }, [])

  if (!navigator) return null

  return (
    <div className="relative flex w-full grow flex-col overflow-y-hidden">
      {!onGoingConversation && !navigator.isServiceSelected && (
        <div className="relative flex w-full justify-center p-6">
          <NavigatorIllustration className="text-primary" />
          <NavigatorBg className="absolute top-0 h-[21rem] w-full drop-shadow-sm" />
        </div>
      )}

      <div
        ref={messageListRef}
        className={cn(
          'flex w-full grow flex-col gap-[3px] overflow-y-auto px-1 pt-3 transition-[padding-bottom] duration-300',
          sessionFeedbackVisible ? 'pb-52' : 'pb-1'
        )}
      >
        {time < ANIMATIONS_TIME.MESSAGE ? (
          <Bubble>{time >= ANIMATIONS_TIME.LOADING && <WritingAnimation />}</Bubble>
        ) : (
          <>
            {messages.map((message, index) => {
              const healthResources = message.sender === NAVIGATOR_AUTHOR.HEALTH_NAVIGATOR ? message.healthResources : null
              const isUser = message.sender === NAVIGATOR_AUTHOR.PATIENT
              const hasFeedback = feedbackEnabled && message.sender === NAVIGATOR_AUTHOR.HEALTH_NAVIGATOR && index > 0
              const hasHealthResources = Boolean(healthResources?.length)
              return (
                <Fragment key={index}>
                  <div className={cn('flex flex-col gap-[3px]', !hasFeedback && !hasHealthResources && 'pb-[9px]')}>
                    <Bubble isUser={isUser} ref={index === messages.length - 1 ? lastMessageRef : null}>
                      <Message message={message} />
                    </Bubble>
                    {hasFeedback && <MessageFeedback message={message} />}
                  </div>

                  {hasHealthResources && (
                    <div className="pb-[9px]">
                      <Bubble hideAvatar>
                        <div className="flex flex-col gap-4">
                          <p>{t('health_navigator.health_resources')}</p>
                          {healthResources?.map((resource, index) => (
                            <HealthResourceButton key={index} resource={resource} />
                          ))}
                        </div>
                      </Bubble>
                    </div>
                  )}
                </Fragment>
              )
            })}

            {isPending && (
              <Bubble>
                <WritingAnimation />
              </Bubble>
            )}
          </>
        )}

        {(isFinished || (time >= ANIMATIONS_TIME.DISCLAIMER && messages.length === 1)) && (
          <p
            className="animate-fade-in px-8 py-2 text-center text-xs text-[#535353]"
            dangerouslySetInnerHTML={{ __html: getSlugTranslation('navigator.disclaimer')! }}
          />
        )}

        {isFinished && (
          <>
            <ConfirmationModal isOpen={isConfirmationModalOpen} onSuccess={handleFinish} onCancel={() => setIsConfirmationModalOpen(false)}>
              <h2 className="px-8 text-center font-bold">{t('health_navigator.new_chat.title')}</h2>
              <p className="text-center">{t('health_navigator.new_chat.description')}</p>
            </ConfirmationModal>

            <button
              data-testid="navigator-new-chat-button"
              className="m-1 flex items-center justify-center gap-4 rounded-lg bg-white p-4 shadow-sm"
              onClick={() => setIsConfirmationModalOpen(true)}
            >
              <RefreshIcon className="text-primary h-5 w-5" />
              <p className="text-primary font-bold uppercase">{t('health_navigator.new_chat.button')}</p>
            </button>
          </>
        )}
      </div>

      {showSessionFeedback && (
        <SessionFeedback conversationId={navigator.healthNavigator.conversationId!} onVisibilityChange={setSessionFeedbackVisible} />
      )}

      {showScrollButton && <ScrollToBottomButton onClick={scrollToBottom} />}

      {!isFinished && (
        <form className="relative flex p-2" onSubmit={sendMessage}>
          <ReactTextareaAutosize
            name="message"
            data-testid="navigator-input"
            className="border-gray focus-visible:border-primary h-15 w-full resize-none rounded-lg border bg-white py-4 pr-12 pl-4 focus-visible:outline-none"
            placeholder={t(messages.length > 1 ? 'health_navigator.placeholder' : 'placeholder.text')!}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onFocus={handleConversationStarted}
          />

          <button
            data-testid="navigator-send-button"
            className="-translate text-primary absolute top-1/2 right-6 h-6 w-6 -translate-y-1/2 transform disabled:text-[#c4c4c4]"
            disabled={!inputMessage?.length || loading}
          >
            <PlaneIcon />
          </button>
        </form>
      )}
    </div>
  )
}
